import { makeStyles } from '@material-ui/styles'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import IconButton from '../button/IconButton'
import { BatchActionsContent } from './BatchActionsContent'
import { GridBatchActionGroupProps, GridBatchActionProps } from './hooks/useGridSelectableRows'

interface FloatingBatchActionBarProps<T> {
  selectedCount?: number
  clearSelectedRows?: () => void
  batchActions?: GridBatchActionProps<T>[]
  dropdownBatchActions?: GridBatchActionGroupProps<T>[]
  isReadOnly?: boolean
}

const useStyles = makeStyles<Theme>((theme) => ({
  '@keyframes slideUp': {
    from: {
      opacity: 0,
      transform: 'translateX(-50%) translateY(16px)',
    },
    to: {
      opacity: 1,
      transform: 'translateX(-50%) translateY(0)',
    },
  },
  floatingActionRoot: {
    position: 'fixed',
    bottom: theme.spacing(3),
    left: '50%',
    padding: theme.spacing(1, 3),
    background: theme.components.table.toolbar,
    border: `1px solid ${theme.components.table.border}`,
    borderRadius: 4,
    boxShadow: '0 0 25px rgba(0,0,0,0.35)',
    maxWidth: 'calc(100% - 16px)',
    width: 'max-content',
    transform: 'translateX(-50%)',
    animation: '$slideUp 150ms ease-out',
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  selectedCount: {
    color: theme.components.table.toolbarColor,
    whiteSpace: 'nowrap',
  },
  clearBtn: {
    border: 'none',
  },
  divider: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    height: 28,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  dropdownActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  actionWrapper: {
    cursor: 'pointer',
  },
  actionWrapperDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  tooltip: {
    display: 'inline-block',
  },
}))

export default function FloatingBatchActionBar<T>({
  selectedCount,
  clearSelectedRows,
  batchActions,
  dropdownBatchActions,
  isReadOnly,
}: FloatingBatchActionBarProps<T>) {
  const classes = useStyles()

  if (!selectedCount) {
    return null
  }

  return (
    <div className={classes.floatingActionRoot}>
      <div className={classes.inner}>
        <Text variant="body2" className={classes.selectedCount}>
          {`${selectedCount} selected`}
        </Text>
        <div className={classes.divider} />
        {!isReadOnly && (
          <>
            <div className={classes.actions}>
              <BatchActionsContent<T>
                batchActions={batchActions}
                dropdownBatchActions={dropdownBatchActions}
                renderDropdownOutside
                actionWrapperClassName={classes.actionWrapper}
                actionWrapperDisabledClassName={classes.actionWrapperDisabled}
                dropdownContainerClassName={classes.dropdownActions}
                tooltipClassName={classes.tooltip}
              />
            </div>
          </>
        )}
        {!isReadOnly && (batchActions?.length > 0 || dropdownBatchActions?.length > 0) && (
          <div className={classes.divider} />
        )}
        <IconButton
          className={classes.clearBtn}
          onClick={() => clearSelectedRows?.()}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && clearSelectedRows?.()}
          icon="times"
          solid={false}
          info="Clear Selection"
        />
      </div>
    </div>
  )
}
